// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import WebViewer, { WebViewerInstance } from '@pdftron/pdfjs-express-viewer';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '../../hooks/use-breakpoint';
import styles from './pdf-viewer.module.css';

interface Props {
  pdfSource: string;
}

const PdfViewer = ({ pdfSource }: Props): JSX.Element => {
  const { t } = useTranslation('common');
  const screens = useBreakpoint();
  const isDesktop = !!screens.lg;
  const viewer = useRef<HTMLDivElement>(null);
  const [viewerInstance, setViewerInstance] = useState<WebViewerInstance>();
  const [isDocumentFound, setIsDocumentFound] = useState(true);

  useEffect(() => {
    if (!viewer.current || viewer.current?.children.length) {
      return;
    }

    WebViewer(
      {
        licenseKey: `${process.env.REACT_APP_PDF_VIEWER_KEY}`,
        path: '/webviewer/lib',
      },
      viewer.current,
    ).then(instance => {
      setViewerInstance(instance);
    });

    return () => {
      viewerInstance?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!viewerInstance || !pdfSource) {
      return;
    }

    fetch(pdfSource, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => {
        if (!res.ok) {
          setIsDocumentFound(false);
          throw Error(res.statusText);
        }

        return res;
      })
      .then(res => res.blob())
      .then(blob => {
        setIsDocumentFound(true);

        const { Tools } = viewerInstance.Core;

        viewerInstance.UI.loadDocument(blob, {
          extension: 'pdf',
        });
        viewerInstance.setFitMode(viewerInstance.FitMode.FitPage);

        viewerInstance.UI.disableTools([Tools.ToolNames.EDIT]);
      })
      .catch(() => {
        viewerInstance.UI.displayErrorMessage(t('pdfViewer.errors.loadDocument'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerInstance, pdfSource]);

  useEffect(() => {
    if (!viewerInstance) {
      return;
    }

    const { Tools } = viewerInstance.Core;

    if (isDesktop) {
      viewerInstance.UI.enableFeatures([viewerInstance.UI.Feature.Copy, viewerInstance.UI.Feature.TextSelection]);
      viewerInstance.UI.setToolMode([Tools.ToolNames.TEXT_SELECT]);
    } else {
      viewerInstance.UI.disableFeatures([viewerInstance.UI.Feature.Copy, viewerInstance.UI.Feature.TextSelection]);
      viewerInstance.UI.setToolMode([Tools.ToolNames.PAN]);
    }
  }, [viewerInstance, isDesktop]);

  return <>{isDocumentFound && <div className={styles.container} ref={viewer}></div>}</>;
};
export default PdfViewer;
